import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  Slider,
  Alert,
  Chip,
  alpha,
  useTheme,
  Avatar,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import {
  SmartToy,
  TrendingUp,
  Psychology,
  EmojiObjects,
  Engineering,
  Speed,
  People,
  IntegrationInstructions,
  ExpandMore,
} from '@mui/icons-material';

interface AgentConfigurationStepProps {
  onNext: (data: any) => void;
  onBack: () => void;
  data: any;
  updateData: (data: any) => void;
}

const AgentConfigurationStep: React.FC<AgentConfigurationStepProps> = ({
  onNext,
  data,
  updateData,
}) => {
  const theme = useTheme();

  const agents = [
    {
      id: 'strategic_forecaster',
      name: 'Strategic Forecaster',
      icon: <TrendingUp />,
      color: theme.palette.primary.main,
      description: 'Predicts demand, identifies trends, and forecasts disruptions',
      capabilities: [
        'Demand forecasting with 95% confidence intervals',
        'Trend analysis and pattern recognition',
        'Scenario planning (best/worst/likely cases)',
        'Early warning system for disruptions',
      ],
      defaultEnabled: true,
      defaultAutonomy: 50,
    },
    {
      id: 'game_theorist',
      name: 'Game Theorist',
      icon: <Psychology />,
      color: theme.palette.secondary.main,
      description: 'Analyzes competitive dynamics and negotiation strategies',
      capabilities: [
        'Competitive strategy analysis',
        'Supplier negotiation tactics',
        'Market positioning recommendations',
        'Strategic decision modeling',
      ],
      defaultEnabled: true,
      defaultAutonomy: 40,
    },
    {
      id: 'creative_catalyst',
      name: 'Creative Catalyst',
      icon: <EmojiObjects />,
      color: theme.palette.warning.main,
      description: 'Generates innovative solutions and process improvements',
      capabilities: [
        'Problem reframing and ideation',
        'Process innovation suggestions',
        'Change management strategies',
        'Cross-industry insights',
      ],
      defaultEnabled: true,
      defaultAutonomy: 30,
    },
    {
      id: 'technical_architect',
      name: 'Technical Architect',
      icon: <Engineering />,
      color: theme.palette.info.main,
      description: 'Designs technical solutions and system improvements',
      capabilities: [
        'System architecture recommendations',
        'Technology stack selection',
        'Integration planning',
        'Technical risk assessment',
      ],
      defaultEnabled: false,
      defaultAutonomy: 40,
    },
    {
      id: 'resource_optimizer',
      name: 'Resource Optimizer',
      icon: <Speed />,
      color: theme.palette.success.main,
      description: 'Optimizes inventory, costs, and resource allocation',
      capabilities: [
        'Inventory optimization',
        'Cost reduction opportunities',
        'Resource allocation strategies',
        'ROI calculations',
      ],
      defaultEnabled: true,
      defaultAutonomy: 60,
    },
    {
      id: 'human_insight',
      name: 'Human Insight Specialist',
      icon: <People />,
      color: theme.palette.error.main,
      description: 'Analyzes stakeholder impacts and change management',
      capabilities: [
        'Stakeholder analysis',
        'Change management planning',
        'Behavioral economics insights',
        'Adoption strategies',
      ],
      defaultEnabled: false,
      defaultAutonomy: 30,
    },
    {
      id: 'knowledge_integrator',
      name: 'Knowledge Integrator',
      icon: <IntegrationInstructions />,
      color: theme.palette.primary.dark,
      description: 'Synthesizes insights from all agents into unified recommendations',
      capabilities: [
        'Multi-agent synthesis',
        'Conflict resolution',
        'Prioritized action plans',
        'Holistic decision support',
      ],
      defaultEnabled: true,
      defaultAutonomy: 50,
    },
  ];

  const [agentConfig, setAgentConfig] = useState(
    agents.reduce((acc, agent) => {
      acc[agent.id] = {
        enabled: data[agent.id]?.enabled ?? agent.defaultEnabled,
        autonomy: data[agent.id]?.autonomy ?? agent.defaultAutonomy,
      };
      return acc;
    }, {} as Record<string, { enabled: boolean; autonomy: number }>)
  );

  const handleToggle = (agentId: string) => {
    const updated = {
      ...agentConfig,
      [agentId]: {
        ...agentConfig[agentId],
        enabled: !agentConfig[agentId].enabled,
      },
    };
    setAgentConfig(updated);
    updateData(updated);
  };

  const handleAutonomyChange = (agentId: string, value: number) => {
    const updated = {
      ...agentConfig,
      [agentId]: {
        ...agentConfig[agentId],
        autonomy: value,
      },
    };
    setAgentConfig(updated);
    updateData(updated);
  };

  const getAutonomyLabel = (value: number) => {
    if (value < 30) return 'Low - Frequent approvals';
    if (value < 60) return 'Medium - Balanced';
    return 'High - More autonomous';
  };

  const handleNext = () => {
    onNext(agentConfig);
  };

  const enabledCount = Object.values(agentConfig).filter((c) => c.enabled).length;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            color: theme.palette.primary.main,
            mx: 'auto',
            mb: 2,
          }}
        >
          <SmartToy sx={{ fontSize: 32 }} />
        </Box>
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
          Configure Your AI Agents
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Choose which agents to activate and set their autonomy levels
        </Typography>
      </Box>

      {/* Summary */}
      <Alert severity="info" sx={{ mb: 4 }}>
        <Typography variant="body2">
          <strong>{enabledCount} of {agents.length} agents enabled.</strong> Each agent
          specializes in different aspects of supply chain management. You can enable/disable
          agents anytime from your dashboard.
        </Typography>
      </Alert>

      {/* Agents */}
      <Grid container spacing={3}>
        {agents.map((agent) => (
          <Grid item xs={12} key={agent.id}>
            <Accordion
              expanded={agentConfig[agent.id].enabled}
              sx={{
                border: 2,
                borderColor: agentConfig[agent.id].enabled
                  ? agent.color
                  : 'divider',
                '&:before': { display: 'none' },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMore />}
                sx={{
                  '&:hover': {
                    bgcolor: alpha(agent.color, 0.05),
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', pr: 2 }}>
                  <Avatar
                    sx={{
                      bgcolor: alpha(agent.color, 0.1),
                      color: agent.color,
                      mr: 2,
                    }}
                  >
                    {agent.icon}
                  </Avatar>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {agent.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {agent.description}
                    </Typography>
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={agentConfig[agent.id].enabled}
                        onChange={() => handleToggle(agent.id)}
                        onClick={(e) => e.stopPropagation()}
                        color="primary"
                      />
                    }
                    label=""
                    sx={{ mr: 0 }}
                  />
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ pt: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    Capabilities:
                  </Typography>
                  <List dense>
                    {agent.capabilities.map((capability, idx) => (
                      <ListItem key={idx} sx={{ py: 0.5 }}>
                        <ListItemText
                          primary={`• ${capability}`}
                          primaryTypographyProps={{ variant: 'body2' }}
                        />
                      </ListItem>
                    ))}
                  </List>

                  <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                      Autonomy Level
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                      {getAutonomyLabel(agentConfig[agent.id].autonomy)}
                    </Typography>
                    <Slider
                      value={agentConfig[agent.id].autonomy}
                      onChange={(_, value) => handleAutonomyChange(agent.id, value as number)}
                      min={0}
                      max={100}
                      marks={[
                        { value: 0, label: 'Low' },
                        { value: 50, label: 'Medium' },
                        { value: 100, label: 'High' },
                      ]}
                      sx={{
                        color: agent.color,
                        '& .MuiSlider-markLabel': {
                          fontSize: '0.75rem',
                        },
                      }}
                    />
                    <Alert severity="info" sx={{ mt: 2 }}>
                      <Typography variant="caption">
                        <strong>What this means:</strong> Higher autonomy = agent can handle more
                        routine decisions automatically. Lower autonomy = you'll review more
                        recommendations before they're executed.
                      </Typography>
                    </Alert>
                  </Box>
                </Box>
              </AccordionDetails>
            </Accordion>
          </Grid>
        ))}
      </Grid>

      {/* Recommendations */}
      <Card sx={{ mt: 4, bgcolor: alpha(theme.palette.success.main, 0.05) }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            💡 Recommended Setup
          </Typography>
          <Typography variant="body2" paragraph>
            For most businesses, we recommend starting with:
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Chip
                label="Strategic Forecaster"
                color="primary"
                size="small"
                sx={{ mr: 1, mb: 1 }}
              />
              <Chip
                label="Resource Optimizer"
                color="primary"
                size="small"
                sx={{ mr: 1, mb: 1 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Chip
                label="Game Theorist"
                color="primary"
                size="small"
                sx={{ mr: 1, mb: 1 }}
              />
              <Chip
                label="Knowledge Integrator"
                color="primary"
                size="small"
                sx={{ mr: 1, mb: 1 }}
              />
            </Grid>
          </Grid>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
            You can enable additional agents as you become familiar with the system.
          </Typography>
        </CardContent>
      </Card>

      {/* Hidden submit */}
      <Box sx={{ display: 'none' }}>
        <button onClick={handleNext}>Submit</button>
      </Box>
    </Box>
  );
};

export default AgentConfigurationStep;
